import React, { useState } from 'react';
import { photoUrl, useCommunity } from './CommunityContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Anchor, Loader2, X } from 'lucide-react';

const INITIAL_COLORS = ['#0e7490', '#0891b2', '#f97316', '#e11d48', '#7c3aed', '#059669'];

export const Avatar = ({ member, size = 44, className = '' }) => {
  const url = member?.photo_path ? photoUrl(member.photo_path) : null;
  const initials = (member?.name || '?')
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const color = INITIAL_COLORS[(member?.name?.length || 0) % INITIAL_COLORS.length];
  return (
    <div
      data-testid="community-avatar"
      className={`rounded-full overflow-hidden flex items-center justify-center font-bold text-white shrink-0 ${className}`}
      style={{ width: size, height: size, background: color, fontSize: size * 0.38 }}
    >
      {url ? (
        <img src={url} alt={member?.name} className="w-full h-full object-cover" />
      ) : (
        initials
      )}
    </div>
  );
};

export const InterestPills = ({ interests = [] }) => (
  <div className="flex flex-wrap gap-1.5">
    {interests.map((i, idx) => (
      <span
        key={idx}
        className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-800 border border-cyan-200"
      >
        {i}
      </span>
    ))}
  </div>
);

export const AvatarStack = ({ members = [], max = 5, size = 28, onClick }) => {
  const shown = members.slice(0, max);
  const extra = members.length - shown.length;
  if (members.length === 0) return null;
  return (
    <div className="flex items-center" data-testid="avatar-stack" onClick={onClick} role={onClick ? 'button' : undefined}>
      <div className="flex -space-x-2">
        {shown.map((m) => (
          <div key={m.id} className="ring-2 ring-white rounded-full" title={m.name}>
            <Avatar member={m} size={size} />
          </div>
        ))}
        {extra > 0 && (
          <div
            className="ring-2 ring-white rounded-full flex items-center justify-center bg-slate-200 text-slate-600 font-semibold"
            style={{ width: size, height: size, fontSize: size * 0.38 }}
          >
            +{extra}
          </div>
        )}
      </div>
    </div>
  );
};

const SUGGESTED = ['Ballo', 'Escursioni', 'Cucina', 'Sport', 'Spa & Relax', 'Musica dal vivo', 'Giochi', 'Fotografia', 'Feste', 'Aperitivi'];

export const AuthDialog = ({ open, onClose, defaultMode = 'login' }) => {
  const { login, register } = useCommunity();
  const [mode, setMode] = useState(defaultMode);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', bio: '', interests: [] });

  React.useEffect(() => {
    if (open) setMode(defaultMode);
  }, [open, defaultMode]);

  const toggleInterest = (i) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i],
    }));

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast.success('Bentornato a bordo!');
      } else {
        if (!form.name.trim()) {
          toast.error('Inserisci il tuo nome');
          setBusy(false);
          return;
        }
        await register({
          email: form.email,
          password: form.password,
          name: form.name,
          bio: form.bio,
          interests: form.interests,
        });
        toast.success('Iscrizione completata! Benvenuto nella Tribù 🌊');
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="community-auth-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-900">
            <Anchor className="w-5 h-5 text-cyan-600" />
            {mode === 'login' ? 'Accedi alla Community' : 'Unisciti alla Community'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'login'
              ? 'Entra per chattare e conoscere i tuoi compagni di crociera.'
              : 'Crea il tuo profilo e inizia a conoscere chi salpa con te.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {mode === 'register' && (
            <div>
              <Label>Nome</Label>
              <Input
                data-testid="auth-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. Mario Rossi"
              />
            </div>
          )}
          <div>
            <Label>Email</Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="tuamail@esempio.it"
            />
          </div>
          <div>
            <Label>Password</Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Almeno 6 caratteri"
            />
          </div>
          {mode === 'register' && (
            <>
              <div>
                <Label>Bio (facoltativa)</Label>
                <Textarea
                  data-testid="auth-bio-input"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  placeholder="Raccontati in due righe..."
                  rows={2}
                />
              </div>
              <div>
                <Label>I tuoi interessi</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SUGGESTED.map((i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleInterest(i)}
                      className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                        form.interests.includes(i)
                          ? 'bg-cyan-600 text-white border-cyan-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-cyan-400'
                      }`}
                    >
                      {i}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          <Button
            data-testid="auth-submit-btn"
            onClick={submit}
            disabled={busy}
            className="w-full bg-cyan-600 hover:bg-cyan-700"
          >
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {mode === 'login' ? 'Accedi' : 'Iscriviti'}
          </Button>
          <p className="text-center text-sm text-slate-500">
            {mode === 'login' ? 'Non hai un account?' : 'Hai già un account?'}{' '}
            <button
              className="text-cyan-600 font-semibold hover:underline"
              onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
              data-testid="auth-toggle-mode"
            >
              {mode === 'login' ? 'Iscriviti' : 'Accedi'}
            </button>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
